import React, { useState, useEffect } from "react";
import { base_url } from "../../Global";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const badgeStyle = (bg, color = "white") => ({
  fontSize: "11px",
  backgroundColor: bg,
  color,
  padding: "2px 7px",
  borderRadius: "999px",
  fontWeight: 700,
  letterSpacing: "0.05em",
});

const mutedPillStyle = {
  fontSize: "11px",
  backgroundColor: "#374151",
  color: "#d1d5db",
  padding: "2px 8px",
  borderRadius: "999px",
  fontWeight: 500,
};

const outlineBtnStyle = {
  background: "none",
  border: "1px solid #4b5563",
  borderRadius: "6px",
  color: "white",
  padding: "6px 14px",
  cursor: "pointer",
  fontSize: "13px",
  flexShrink: 0,
};

const solidBtnStyle = (bg) => ({
  padding: "7px 14px",
  borderRadius: "6px",
  border: "none",
  cursor: "pointer",
  backgroundColor: bg,
  color: "white",
  fontSize: "13px",
  fontWeight: 500,
  flexShrink: 0,
});

const STATUS_BADGES = {
  warned: badgeStyle("#d97706"),
  suspended: badgeStyle("#dc2626"),
  reactivated: badgeStyle("#059669"),
};

const ReportedUsers = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  const fetchReports = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const headers = { "Content-Type": "application/json" };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await fetch(`${base_url}/auth/reports`, { headers });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `Server responded with ${response.status}`);
      }
      const data = await response.json();
      const list = data.reports || [];

      const activeReports = list.filter(
        (r) => !r.status || r.status !== "dismissed"
      );

      activeReports.sort((a, b) => {
        const ta = new Date(a.createdAt || 0).getTime();
        const tb = new Date(b.createdAt || 0).getTime();
        return tb - ta;
      });

      setReports(activeReports);
    } catch (e) {
      console.error("Error fetching LXDC reports:", e);
      toast.error("Failed to load reported users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);


  const setActionBusy = (id, val) =>
    setActionLoading((prev) => ({ ...prev, [id]: val }));


  const handleWarn = async (report) => {
    const reportId = report._id || report.id;
    const userObj = report.reportedUser || {};
    const userName = userObj.username || report.reportedUserName || "User";

    setActionBusy(reportId, true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const headers = { "Content-Type": "application/json" };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      await fetch(`${base_url}/auth/reports/updateStatus`, {
        method: "POST",
        headers,
        body: JSON.stringify({ reportId, status: "warned" }),
      });

      toast.success(`⚠️ In-app warning sent to ${userName}`);
      setReports((prev) =>
        prev.map((r) =>
          (r._id || r.id) === reportId ? { ...r, status: "warned" } : r
        )
      );
    } catch (e) {
      console.error("Warn error:", e);
      toast.error("Failed to send warning");
    } finally {
      setActionBusy(reportId, false);
    }
  };

  const handleSuspend = async (report) => {
    const reportId = report._id || report.id;
    const userObj = report.reportedUser || {};
    const userId = userObj._id || report.reportedUserId || report.reportedUser;
    const userName = userObj.username || report.reportedUserName || "User";

    setActionBusy(reportId, true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const headers = { "Content-Type": "application/json" };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      if (userId) {
        await fetch(`${base_url}/auth/blockOrUnblock`, {
          method: "POST",
          headers,
          body: JSON.stringify({ userId, action: "block" }),
        });
      }

      await fetch(`${base_url}/auth/reports/updateStatus`, {
        method: "POST",
        headers,
        body: JSON.stringify({ reportId, status: "suspended" }),
      });

      toast.success(`🚫 ${userName} suspended`);
      setReports((prev) =>
        prev.map((r) =>
          (r._id || r.id) === reportId ? { ...r, status: "suspended" } : r
        )
      );
    } catch (e) {
      console.error("Suspend error:", e);
      toast.error("Failed to suspend user");
    } finally {
      setActionBusy(reportId, false);
    }
  };

  const handleReactivate = async (report) => {
    const reportId = report._id || report.id;
    const userObj = report.reportedUser || {};
    const userId = userObj._id || report.reportedUserId || report.reportedUser;
    const userName = userObj.username || report.reportedUserName || "User";

    setActionBusy(reportId, true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const headers = { "Content-Type": "application/json" };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      if (userId) {
        await fetch(`${base_url}/auth/blockOrUnblock`, {
          method: "POST",
          headers,
          body: JSON.stringify({ userId, action: "unblock" }),
        });
      }

      await fetch(`${base_url}/auth/reports/updateStatus`, {
        method: "POST",
        headers,
        body: JSON.stringify({ reportId, status: "reactivated" }),
      });

      toast.success(`✅ ${userName} reactivated`);
      setReports((prev) =>
        prev.map((r) =>
          (r._id || r.id) === reportId ? { ...r, status: "reactivated" } : r
        )
      );
    } catch (e) {
      console.error("Reactivate error:", e);
      toast.error("Failed to reactivate user");
    } finally {
      setActionBusy(reportId, false);
    }
  };

  const handleDismiss = async (report) => {
    const reportId = report._id || report.id;
    setActionBusy(reportId, true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const headers = { "Content-Type": "application/json" };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      await fetch(`${base_url}/auth/reports/updateStatus`, {
        method: "POST",
        headers,
        body: JSON.stringify({ reportId, status: "dismissed" }),
      });

      toast.info(`Report dismissed`);
      setReports((prev) => prev.filter((r) => (r._id || r.id) !== reportId));
    } catch (e) {
      toast.error("Failed to dismiss report");
    } finally {
      setActionBusy(reportId, false);
    }
  };

  const handleDeleteUser = async (report) => {
    if (
      !window.confirm(
        "Are you sure you want to permanently delete this user? This action cannot be undone."
      )
    )
      return;

    const reportId = report._id || report.id;
    const userObj = report.reportedUser || {};
    const userId = userObj._id || report.reportedUserId || report.reportedUser;
    const userName = userObj.username || report.reportedUserName || "User";

    setActionBusy(reportId, true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const headers = { "Content-Type": "application/json" };
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      if (userId) {
        await fetch(`${base_url}/auth/deleteUser`, {
          method: "POST",
          headers,
          body: JSON.stringify({ userId }),
        });
      }

      await fetch(`${base_url}/auth/reports/${reportId}`, {
        method: "DELETE",
        headers,
      });

      toast.success(`🗑️ ${userName} deleted permanently`);
      setReports((prev) => prev.filter((r) => (r._id || r.id) !== reportId));
    } catch (e) {
      console.error("Delete user error:", e);
      toast.error("Failed to delete user");
    } finally {
      setActionBusy(reportId, false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "—";
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "—";
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#1f2937",
        borderRadius: "12px",
        padding: "20px",
        color: "white",
      }}
    >
      <ToastContainer />

      {/* Header */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, fontSize: "clamp(1.25rem, 4vw, 2rem)", textTransform: "capitalize", fontWeight: 700 }}>
            Reported Users
          </h1>
          {!loading && (
            <div style={{ border: "0.6px solid white", borderRadius: "6px", padding: "8px 12px", fontSize: "14px" }}>
              Reports: {reports.length}
            </div>
          )}
        </div>

        <button onClick={fetchReports} style={outlineBtnStyle}>
          Refresh
        </button>
      </div>

      {/* Body */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#9ca3af" }}>
          Loading reports…
        </div>
      ) : reports.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#9ca3af" }}>
          ✅ No reported users
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px", maxHeight: "70vh", overflowY: "auto" }}>
          {reports.map((report) => {
            const reportId = report._id || report.id;
            const busy = actionLoading[reportId];
            const reported = report.reportedUser || {};
            const reporter = report.reporter || {};

            const reportedName =
              reported.username || report.reportedUserName || "Unknown";
            const reportedEmail =
              reported.email || report.reportedUserEmail || "";
            const reportedImage =
              reported.profileImage || report.reportedUserImage || "";

            const reporterName =
              reporter.username || report.reporterName || "Anonymous";

            return (
              <div
                key={reportId}
                style={{
                  backgroundColor: "#111827",
                  borderRadius: "8px",
                  padding: "14px 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                {/* Left: avatar, name, badges, reporter/reason/date */}
                <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1, minWidth: 0 }}>
                  {reportedImage ? (
                    <img
                      src={reportedImage}
                      alt={reportedName}
                      onError={(e) => { e.target.style.display = "none"; }}
                      style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "1px solid #374151" }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "#374151",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: "16px",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {reportedName[0]?.toUpperCase() || "?"}
                    </div>
                  )}

                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", fontWeight: 600, fontSize: "15px" }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {reportedName}
                      </span>
                      {report.status && report.status !== "pending" && (
                        <span style={STATUS_BADGES[report.status] || mutedPillStyle}>
                          {report.status.toUpperCase()}
                        </span>
                      )}
                      <span style={mutedPillStyle} title={report.reason}>
                        🚩 {report.reason || "No reason given"}
                      </span>
                      <span style={mutedPillStyle}>
                        🕒 {formatDate(report.createdAt)}
                      </span>
                      <span style={mutedPillStyle} title={reporter.email || ""}>
                        👤 Reported by {reporterName}
                      </span>
                    </div>

                    <div style={{ fontSize: "13px", color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "320px" }}>
                      {reportedEmail || (reported._id ? `${reported._id.slice(0, 10)}…` : "No email")}
                    </div>

                    {report.details && (
                      <div style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "320px" }} title={report.details}>
                        {report.details}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: action buttons */}
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button
                    disabled={busy}
                    onClick={() => handleWarn(report)}
                    style={{ ...solidBtnStyle(report.status === "warned" ? "#92400e" : "#d97706"), opacity: busy ? 0.5 : 1, cursor: busy ? "not-allowed" : "pointer" }}
                  >
                    {report.status === "warned" ? "Warned" : "Warn"}
                  </button>

                  {report.status === "suspended" ? (
                    <button
                      disabled={busy}
                      onClick={() => handleReactivate(report)}
                      style={{ ...solidBtnStyle("#059669"), opacity: busy ? 0.5 : 1, cursor: busy ? "not-allowed" : "pointer" }}
                    >
                      Reactivate
                    </button>
                  ) : (
                    <button
                      disabled={busy}
                      onClick={() => handleSuspend(report)}
                      style={{ ...solidBtnStyle("#dc2626"), opacity: busy ? 0.5 : 1, cursor: busy ? "not-allowed" : "pointer" }}
                    >
                      Suspend
                    </button>
                  )}

                  <button
                    disabled={busy}
                    onClick={() => handleDismiss(report)}
                    style={{ ...outlineBtnStyle, opacity: busy ? 0.5 : 1, cursor: busy ? "not-allowed" : "pointer" }}
                  >
                    Dismiss
                  </button>

                  <button
                    disabled={busy}
                    onClick={() => handleDeleteUser(report)}
                    title="Delete User"
                    style={{ ...solidBtnStyle("#ef4444"), opacity: busy ? 0.5 : 1, cursor: busy ? "not-allowed" : "pointer" }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReportedUsers;